import { getApiBaseUrl, getAuthToken } from "./fetchApi";

/**
 * AiNpc アシスタント（全タブ横断）への SSE ストリーミング送信。
 *
 * バックエンド: POST /ai-npc/assistant/（text/event-stream）
 * 会話履歴はサーバー非保存のため、呼び出し側が messages 全体を毎回渡す。
 *
 * @param {{tab: string, object_id?: string}} context 現在の画面コンテキスト
 * @param {Array<{role: "user"|"assistant", content: string}>} messages
 * @param {{
 *   onToken: (t:string)=>void,
 *   onDone: (d:object)=>void,
 *   onError: (e:string)=>void,
 *   onToolCall?: (name:string)=>void,
 *   onObjectUpdated?: (d:{tab:string,id:string,content_version?:number,changed_fields?:string[],created?:boolean})=>void,
 *   onOpenObject?: (d:{tab:string,id:string})=>void,
 * }} handlers
 */
export async function sendAssistantMessageStream(
  context,
  messages,
  { onToken, onDone, onError, onToolCall, onObjectUpdated, onOpenObject }
) {
  const API_BASE = getApiBaseUrl();
  const idToken = await getAuthToken();

  try {
    const response = await fetch(`${API_BASE}/ai-npc/assistant/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ context, messages }),
      credentials: "include",
    });

    if (!response.ok) {
      let msg = "リクエストに失敗しました";
      try {
        const err = await response.json();
        msg = err.error || msg;
      } catch (_) {
        /* non-JSON error body */
      }
      onError(msg);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === "token") onToken(data.content);
          else if (data.type === "tool_call") onToolCall?.(data.name);
          else if (data.type === "object_updated") onObjectUpdated?.(data);
          else if (data.type === "open_object") onOpenObject?.(data);
          else if (data.type === "done") onDone(data);
          else if (data.type === "error") onError(data.error);
        } catch (_) {
          // malformed JSON line — skip
        }
      }
    }
  } catch (error) {
    onError(error.message || String(error));
  }
}

/**
 * アシスタントの回答が役に立たなかった（👎）を記録する。
 * バックエンド: POST /ai-npc/assistant/feedback/（会話は非保存のため履歴を渡す）
 * @param {{tab: string, object_id?: string}} context
 * @param {Array<{role: string, content: string}>} messages そのバブルまでの履歴
 */
export async function sendAssistantFeedback(context, messages) {
  const API_BASE = getApiBaseUrl();
  const idToken = await getAuthToken();
  const response = await fetch(`${API_BASE}/ai-npc/assistant/feedback/`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ context, messages }),
    credentials: "include",
  });
  if (!response.ok) throw new Error("feedback failed");
}
